import { ReactElement, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Box } from '@twilio-paste/core';
import { styled } from '@twilio-paste/core/styling-library';
import {
  ConversationId,
  WiseOwl,
  WiseOwlProvider,
  WiseOwlUiEvent,
  WiseOwlUiUser,
} from '@twiliointernal/ui-wise-owl';

import { OSC_WO_DISPLAY_NAME } from '@twiliointernal/e2-commons-shared-definitions';
import { useSegment } from '@twiliointernal/e2-commons-shared-hooks';
import { useUserDetails } from '../../../hooks';
import { setFetchArgs } from '../../../utils';

import styles from './wiseOwlViewV2.module.css';
import { constants } from '../definitions';

const StyledBox = styled(Box)`
  > .paste-theme-provider > div {
    height: 100%;
  }
`;

// TODO: ConversationId/WiseOwlUiUser intellisense do not work, need to resolve in WOF bundling
const WiseOwlViewV2 = (): ReactElement => {
  const navigate = useNavigate();
  const { chatId: chatIdFromRoute } = useParams<'chatId'>() as {
    chatId?: ConversationId;
  };
  const { account, csrf, user, isLoading, isLoggedIn } = useUserDetails();

  const { stargateUngatedUserId, trackEvent } = useSegment({
    accountSid: account?.sid,
    userSid: user?.sid,
    isLoggedIn,
    isLoading,
  });
  const wiseOwlUser: WiseOwlUiUser = {
    firstName: user?.firstName || 'M',
    lastName: user?.lastName || 'E',
    accountSid: account?.sid,
    userSid: user?.sid,
    emailAddr: user?.emailAddr,
    sessionId: stargateUngatedUserId,
  };

  useEffect(() => {
    /**
     * User is not logged in and chatId is present in route => redirect
     * This is because we match a chatId by a userId in the WO API, which
     * cannot be done for ungated users because the stargateUngatedUserId
     * can be inconsistent.
     */
    if (!isLoggedIn && chatIdFromRoute) {
      navigate('/');
    }
  }, [isLoggedIn]);

  const handleFetchArgs = (fetchArgs, requestService) => {
    const updatedFetchArgs = setFetchArgs({
      fetchArgs,
      requestService,
      csrf,
      isLoggedIn,
      stargateUngatedUserId,
    });

    return updatedFetchArgs;
  };

  const handleEvent = (event: WiseOwlUiEvent): void => {
    trackEvent(event.type, {
      user,
      ...event.data,
    });

    switch (event.type) {
      case 'createConversation': {
        navigate(`/chat/${event.data.conversationId}`);
        break;
      }

      case 'sendMessage': {
        break;
      }
    }
  };

  return (
    <StyledBox as="div" className={styles.wiseOwlContainer}>
      <WiseOwlProvider requestTransformer={handleFetchArgs}>
        <WiseOwl
          applicationId="help-center-application"
          displayName={OSC_WO_DISPLAY_NAME}
          user={wiseOwlUser}
          conversationId={chatIdFromRoute}
          handleEvent={handleEvent}
          systemContext={{}}
          surveyId={constants.SURVEY_ID}
        />
      </WiseOwlProvider>
    </StyledBox>
  );
};

export default WiseOwlViewV2;
